// Types
interface ApiConfig {
  apiUrl?: string;
  authToken?: string;
  apiHeader?: Record<string, string>;
  canEdit?: boolean;
  canDelete?: boolean;
  canTemporary?: boolean;
  isAdmin?: boolean;
}

interface C2Data {
  isAdmin: boolean | string;
  isAdminProject: boolean | string;
  canEdit: boolean | string;
  canDelete: boolean | string;
  canTemporary: boolean | string;
  External: boolean | string;
}

interface Layer {
  LayerId: number;
  [key: string]: any;
}

interface Conversation {
  OwnedById?: number;
  [key: string]: any;
}

/**
 * Helper class para manejo de localStorage y sessionStorage
 * Proporciona métodos estáticos para acceder a la configuración de la aplicación
 */
class CorgeeStorageHelper
{
  private static readonly APP_PREFIX = 'C2';

  /**
   * Obtiene el prefijo de la aplicación para keys de storage
   */
  static appPrefix(): string
  {
    return this.APP_PREFIX;
  }

  /**
   * Obtiene el nombre dado del usuario
   */
  static givenName(): string | null
  {
    return localStorage.getItem(`${this.appPrefix()}_givenname`);
  }

  /**
   * Obtiene el nombre completo del usuario para mostrar en UI.
   * Intenta C2_givenname primero; si está vacío, decodifica el JWT para obtener
   * el claim 'name' o 'given_name'; como último recurso usa C2_username.
   */
  static fullName(): string
  {
    const stored = localStorage.getItem(`${this.appPrefix()}_givenname`);
    if (stored) return stored;

    // Fallback: decodificar el payload del JWT
    const token = localStorage.getItem(`${this.appPrefix()}_access_token`);
    if (token)
    {
      try
      {
        const parts = token.split('.');
        if (parts.length === 3)
        {
          const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));
          const name =
            payload.name || payload.given_name || payload.givenname || payload.unique_name;
          if (name) return name;
        }
      }
      catch
      {
        // ignorar error de decodificación
      }
    }

    return localStorage.getItem(`${this.appPrefix()}_username`) || '';
  }

  /**
   * Obtiene el límite máximo de carga de conversación
   */
  static conv_maxAllowedUpload(): string | null
  {
    return localStorage.getItem(`${this.appPrefix()}_conv_maxAllowedUpload`);
  }

  /**
   * Obtiene el ID de la persona actual
   */
  static personId(): number
  {
    const id = localStorage.getItem(`${this.appPrefix()}_personid`);
    return parseInt(id || '0', 10);
  }

  /**
   * Obtiene la configuración completa de la API
   */
  static getApiConfig(propsApiConfig?: ApiConfig): ApiConfig
  {
    const config: ApiConfig = propsApiConfig || {};

    config.apiUrl = this.apiURL(config);
    config.authToken = this.getToken(config);
    config.apiHeader = this.apiHeader(config);
    config.canEdit = this.canEdit(config);
    config.canDelete = this.canDelete(config);
    config.canTemporary = this.canTemporary(config);

    return config;
  }

  /**
   * Obtiene la URL de la API
   */
  static apiURL(propsApiConfig?: ApiConfig): string
  {
    try
    {
      const url = propsApiConfig?.apiUrl ?? localStorage.getItem(`${this.appPrefix()}_apiURL`);
      return url?.toString() || '';
    }
    catch (e)
    {
      console.error('Error getting API URL:', e);
      return '';
    }
  }

  /**
   * Genera los headers para las peticiones API
   */
  static apiHeader(propsApiConfig?: ApiConfig): Record<string, string>
  {
    const token = propsApiConfig?.authToken ?? this.getToken();
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Obtiene el token de autenticación
   */
  static getToken(propsApiConfig?: ApiConfig): string
  {
    return (
      propsApiConfig?.authToken ?? localStorage.getItem(`${this.appPrefix()}_access_token`) ?? ''
    );
  }

  /**
   * Verifica si el usuario está autenticado
   */
  static isAuthenticated(propsApiConfig?: ApiConfig): boolean
  {
    const token = localStorage.getItem(`${this.appPrefix()}_access_token`);

    if (token && token !== '')
    {
      return true;
    }

    if (propsApiConfig?.authToken && propsApiConfig.authToken !== '')
    {
      return true;
    }

    return false;
  }

  /**
   * Obtiene datos de C2 desde sessionStorage
   */
  private static getC2Data(): C2Data | null
  {
    try
    {
      const data = sessionStorage.getItem('C2_data');
      return data ? JSON.parse(data) : null;
    }
    catch (e)
    {
      console.error('Error parsing C2_data:', e);
      return null;
    }
  }

  /**
   * Obtiene datos de Tenant Data
   */
  static getTenantData(): any | null
  {
    try
    {
      const data = localStorage.getItem('C2_tenantData');
      return data ? JSON.parse(data) : null;
    }
    catch (e)
    {
      console.error('Error parsing getTenantData:', e);
      return null;
    }
  }

  /**
   * Convierte un valor a booleano de forma segura
   */
  private static toBoolean(value: boolean | string | undefined | null): boolean
  {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string')
    {
      return value.toLowerCase() === 'true';
    }
    return false;
  }

  /**
   * Verifica si el usuario es administrador
   */
  static isAdmin(propsApiConfig?: ApiConfig): boolean
  {
    try
    {
      if (propsApiConfig?.isAdmin !== undefined && propsApiConfig.isAdmin !== null)
      {
        return this.toBoolean(propsApiConfig.isAdmin);
      }

      const data = this.getC2Data();
      return data ? this.toBoolean(data.isAdmin) : false;
    }
    catch (e)
    {
      console.error('Error checking isAdmin:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario es administrador de proyecto
   */
  static isAdminProject(): boolean
  {
    try
    {
      const data = this.getC2Data();
      return data ? this.toBoolean(data.isAdminProject) : false;
    }
    catch (e)
    {
      console.error('Error checking isAdminProject:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario es administrador de PTOApprover
   */
  static isPTOApprover(): boolean
  {
    try
    {
      const data = sessionStorage.getItem('C2_PTOApprover');
      return data ? this.toBoolean(data) : false;
    }
    catch (e)
    {
      console.error('Error checking PTOApproverProject:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario puede editar
   */
  static canEdit(propsApiConfig?: ApiConfig): boolean
  {
    try
    {
      if (propsApiConfig?.canEdit !== undefined && propsApiConfig.canEdit !== null)
      {
        return this.toBoolean(propsApiConfig.canEdit);
      }

      const data = this.getC2Data();
      return data ? this.toBoolean(data.canEdit) : false;
    }
    catch (e)
    {
      console.error('Error checking canEdit:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario puede editar como propietario
   */
  static canEditOwner(conv: Conversation, personId: number, isFromActual?: boolean): boolean
  {
    try
    {
      let data = this.getC2Data();

      if (!data)
      {
        const localData = localStorage.getItem('C2_data');
        data = localData ? JSON.parse(localData) : null;
      }

      if (!data) return false;

      const isAdmin = this.toBoolean(data.isAdmin);

      if (isFromActual)
      {
        return isAdmin;
      }

      const canEditOrTemporary = this.toBoolean(data.canEdit) || this.toBoolean(data.canTemporary);

      if (conv.OwnedById === personId && canEditOrTemporary)
      {
        return true;
      }

      return isAdmin;
    }
    catch (e)
    {
      console.error('Error checking canEditOwner:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario puede hacer cambios temporales
   */
  static canTemporary(propsApiConfig?: ApiConfig): boolean
  {
    try
    {
      if (propsApiConfig?.canTemporary !== undefined && propsApiConfig.canTemporary !== null)
      {
        return this.toBoolean(propsApiConfig.canTemporary);
      }

      const data = this.getC2Data();
      return data ? this.toBoolean(data.canTemporary) : false;
    }
    catch (e)
    {
      console.error('Error checking canTemporary:', e);
      return false;
    }
  }

  /**
   * Verifica si el usuario puede eliminar
   */
  static canDelete(propsApiConfig?: ApiConfig): boolean
  {
    try
    {
      if (propsApiConfig?.canDelete !== undefined && propsApiConfig.canDelete !== null)
      {
        return this.toBoolean(propsApiConfig.canDelete);
      }

      const data = this.getC2Data();
      return data ? this.toBoolean(data.canDelete) : false;
    }
    catch (e)
    {
      console.error('Error checking canDelete:', e);
      return false;
    }
  }

  /**
   * Obtiene el ID de la capa actual
   */
  static layerId(): string | null
  {
    return localStorage.getItem(`${this.appPrefix()}_layerid`);
  }

  // Default security group id (C2_defaultGroupId)
  static defaultGroupId(): string | null
  {
    return localStorage.getItem(`${this.appPrefix()}_defaultGroupId`);
  }

  /**
   * Obtiene la lista de capas a ocultar
   */
  static getLayersToHide(): Layer[] | null
  {
    try
    {
      const layers = sessionStorage.getItem('C2_LayersToHide');
      return layers ? JSON.parse(layers) : null;
    }
    catch (e)
    {
      console.error('Error getting layers to hide:', e);
      return null;
    }
  }

  /**
   * Verifica si una capa está oculta
   */
  static isLayerHidden(layerId: number): boolean
  {
    const layersToHide = this.getLayersToHide();
    if (!layersToHide) return false;
    return layersToHide.some(layer => layer.LayerId === layerId);
  }

  /**
   * Verifica si los tooltips de ayuda están habilitados
   */
  static enableHelpTooltips(): boolean
  {
    const value = localStorage.getItem(`${this.appPrefix()}_enableHelpTooltips`);
    return value === 'true';
  }

  /**
   * Obtiene todas las capas disponibles
   */
  static getAllLayers(): Layer[]
  {
    try
    {
      const layers = sessionStorage.getItem('C2_AllLayers');
      return layers ? JSON.parse(layers) : [];
    }
    catch (e)
    {
      console.error('Error getting all layers:', e);
      return [];
    }
  }

  /**
   * Verifica si el usuario es externo
   */
  static isUserExternal(): boolean
  {
    try
    {
      const data = this.getC2Data();
      return data ? this.toBoolean(data.External) : false;
    }
    catch (e)
    {
      console.error('Error checking isUserExternal:', e);
      return false;
    }
  }

  /**
   * Obtiene el nombre de configuración del proyecto
   */
  static getProjectNameConfig(): string
  {
    const projectName = sessionStorage.getItem('C2_ProjectNameconfig');

    if (!projectName || projectName === '')
    {
      return 'Project';
    }

    // Si termina en 's', remover la última letra
    if (projectName.endsWith('s'))
    {
      return projectName.substring(0, projectName.length - 1);
    }

    return projectName;
  }

  /**
   * Obtiene el ConfigValueId de fases
   */
  static configValueIdPhases(): number | null
  {
    // Discards NaN from values persisted as the literal "undefined"
    const value = localStorage.getItem('C2_ConfigValueIdPhases');
    const parsed = value ? parseInt(value, 10) : NaN;

    if (!Number.isNaN(parsed) && parsed !== 0)
    {
      return parsed;
    }

    // Both caches are written by MainLayout, absent outside its routes
    try
    {
      const data = JSON.parse(sessionStorage.getItem('C2_data') || '');
      const fromPerson = Number(data?.ConfigValueIdPhases);
      return !Number.isNaN(fromPerson) && fromPerson !== 0 ? fromPerson : null;
    }
    catch (_e)
    {
      return null;
    }
  }

  /**
   * Establece el token de autenticación
   */
  static setToken(token: string): void
  {
    localStorage.setItem(`${this.appPrefix()}_access_token`, token);
  }

  /**
   * Establece el ID de persona
   */
  static setPersonId(id: number): void
  {
    localStorage.setItem(`${this.appPrefix()}_personid`, id.toString());
  }

  /**
   * Establece la URL de la API
   */
  static setApiURL(url: string): void
  {
    localStorage.setItem(`${this.appPrefix()}_apiURL`, url);
  }

  /**
   * Carga datos en sessionStorage/localStorage desde un objeto de configuración
   * Usado para inicializar el storage cuando se monta un componente
   */
  static loadData(storageData: any): void
  {
    if (!storageData) return;

    try
    {
      // Load C2_data
      if (storageData.data)
      {
        sessionStorage.setItem('C2_data', JSON.stringify(storageData.data));
      }

      // Load allLayers
      if (storageData.allLayers)
      {
        sessionStorage.setItem('C2_AllLayers', JSON.stringify(storageData.allLayers)); // Prevent Crash when create Conversation
      }

      // Load layersToHide
      if (storageData.layersToHide !== undefined)
      {
        sessionStorage.setItem('C2_LayersToHide', JSON.stringify(storageData.layersToHide));
      }

      // Load personid
      if (storageData.personid !== undefined)
      {
        this.setPersonId(storageData.personid);
      }

      // Load layerId
      if (storageData.layerId !== undefined)
      {
        localStorage.setItem(`${this.appPrefix()}_layerid`, storageData.layerId.toString());
      }

      // Load conv_maxAllowedUpload
      if (storageData.conv_maxAllowedUpload !== undefined)
      {
        localStorage.setItem(
          `${this.appPrefix()}_conv_maxAllowedUpload`,
          storageData.conv_maxAllowedUpload.toString(),
        );
      }

      // Load enableChat
      if (storageData.enableChat !== undefined)
      {
        localStorage.setItem(`${this.appPrefix()}_enableChat`, storageData.enableChat.toString());
      }
    }
    catch (e)
    {
      console.error('Error loading storage data:', e);
    }
  }
}

export default CorgeeStorageHelper;

// Export de tipos para uso externo
export type { ApiConfig, C2Data, Layer, Conversation };
